import { axiosInstance } from '../clients/axiosClient';
import { API_V1_PREFIX } from '../utils/constants';

export type Team = {
  id: string;
  name: string;
  description: string;
};

export type TeamsListResponse = {
  teams: Team[];
  totalCount: number;
};

export type CreateTeamRequest = {
  name: string;
  description: string;
};

export type CreateTeamResponse = {
  id?: string;
  team_id?: string;
  status?: string;
};

export type UpdateTeamRequest = {
  team_id: string;
  name: string;
  description: string;
};

export type UpdateTeamResponse = {
  id?: string;
  team_id?: string;
  status?: string;
};

export type GetTeamByIdRequest = {
  team_id: string;
};

const parseError = (error: any): Error => {
  const responseData = error?.response?.data;
  const detail =
    Array.isArray(responseData?.detail) && responseData.detail.length > 0
      ? responseData.detail[0]?.msg || responseData.detail[0]
      : responseData?.detail;
  const msg =
    detail ||
    responseData?.detail ||
    responseData?.message ||
    responseData?.description ||
    error?.message ||
    'Request failed';
  return new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
};

const normalizeTeam = (raw: any): Team => ({
  id: String(raw?.id ?? raw?.team_id ?? ''),
  name: String(raw?.name ?? ''),
  description: String(raw?.description ?? ''),
});

export const getTeamsList = async (
  offset: number = 0,
  limit: number = 10
): Promise<TeamsListResponse> => {
  try {
    const params = new URLSearchParams({
      limit: String(limit),
      offset: String(offset),
    });
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}teams/list/?${params.toString()}`
    );
    const teams = Array.isArray(data)
      ? data.map(normalizeTeam)
      : Array.isArray(data?.teams)
        ? data.teams.map(normalizeTeam)
        : [];
    const totalCount =
      Number(data?.total_count ?? data?.['total count'] ?? teams.length) || 0;
    return { teams, totalCount };
  } catch (error: any) {
    throw parseError(error);
  }
};

export const createTeam = async (payload: {
  name: string;
  description: string;
}): Promise<Team> => {
  try {
    const createTeamRequestObject: CreateTeamRequest = {
      name: payload.name,
      description: payload.description,
    };

    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}teams/`,
      createTeamRequestObject
    );
    const responseData: CreateTeamResponse = data;

    return {
      id: String(responseData?.id ?? responseData?.team_id ?? ''),
      name: payload.name,
      description: payload.description,
    };
  } catch (error: any) {
    throw parseError(error);
  }
};

export const updateTeam = async (
  teamId: string,
  payload: { name: string; description: string }
): Promise<Team> => {
  try {
    const updateTeamRequestObject: UpdateTeamRequest = {
      team_id: teamId,
      name: payload.name,
      description: payload.description,
    };

    const { data } = await axiosInstance.patch(
      `${API_V1_PREFIX}teams/`,
      updateTeamRequestObject
    );
    const responseData: UpdateTeamResponse = data;

    return {
      id: String(responseData?.id ?? responseData?.team_id ?? teamId),
      name: payload.name,
      description: payload.description,
    };
  } catch (error: any) {
    throw parseError(error);
  }
};

export const getTeamById = async (teamId: string): Promise<Team> => {
  try {
    const getTeamByIdRequestObject: GetTeamByIdRequest = {
      team_id: teamId,
    };

    const { data } = await axiosInstance.post(
      `${API_V1_PREFIX}teams/get/`,
      getTeamByIdRequestObject
    );

    return normalizeTeam(data?.team ?? data);
  } catch (error: any) {
    throw parseError(error);
  }
};
